import React, { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  makeStyles,
  TablePagination,
  TextField,
  Typography
} from '@material-ui/core'
import { ArrowBack, Search as SearchIcon } from '@material-ui/icons'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment-timezone'

import attendanceSlice, { fetchAttendeesAttendance, decideAttendanceRecord } from 'store/attendance'
import { toast } from 'utils/notify'
import { Roles } from 'constants/index'
import ConfirmationModal from 'components/ConfirmationModal'
import Wrapper from 'components/Wrapper'
import ResourceNotFound from 'components/ResourceNotFound'

const STATUS_COLORS = {
  approved: { bg: '#e8f5e9', color: '#2e7d32' },
  pending: { bg: '#fff3e0', color: '#ef6c00' },
  rejected: { bg: '#ffebee', color: '#c62828' },
  superseded: { bg: '#eceff1', color: '#546e7a' }
}

const StatusChip = ({ status }) => {
  const style = STATUS_COLORS[status] || STATUS_COLORS.pending
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: style.color,
        background: style.bg,
        borderRadius: 10,
        padding: '3px 8px',
        whiteSpace: 'nowrap',
        textTransform: 'uppercase'
      }}>
      {status}
    </span>
  )
}

const formatTargetWindow = (record) => {
  if (!record?.targetStart || !record?.targetEnd) return ''
  const start = moment(record.targetStart)
  const end = moment(record.targetEnd)
  return `${start.format('h:mm A')} - ${end.format('h:mm A')}`
}

const useQuery = () => new URLSearchParams(useLocation().search)

const PendingAttendancePage = () => {
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()
  const query = useQuery()

  const { user } = useSelector((state) => state.auth)
  const { conference } = useSelector((state) => state.conference)
  const conferenceId =
    query.get('conferenceId') || (user?.role !== Roles.SUPER_ADMIN ? conference?.id : '')

  const { inProgress, attendeesAttendance } = useSelector((state) => state.attendance)

  const [searchTerm, setSearchTerm] = useState('')
  const [activeDayFilter, setActiveDayFilter] = useState(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [decidingId, setDecidingId] = useState(null)
  const [rejectingId, setRejectingId] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [reApproveId, setReApproveId] = useState(null)

  const activeConfId = user?.role === Roles.SUPER_ADMIN ? conferenceId : conference?.id

  useEffect(() => {
    if (activeConfId) {
      dispatch(fetchAttendeesAttendance(activeConfId))
    }
    return () => {
      dispatch(attendanceSlice.actions.setAttendeesAttendance([]))
    }
  }, [activeConfId])

  const conferenceDates = useMemo(
    () => attendeesAttendance[0]?.attendance.map(({ date }) => date) || [],
    [attendeesAttendance]
  )

  const goBack = () => history.goBack()

  const handleDecide = async (attendanceId, decision, reason = '') => {
    setDecidingId(attendanceId)
    try {
      await decideAttendanceRecord(attendanceId, decision, user?.id, reason)
      toast.success(`Attendance ${decision}.`)
      if (activeConfId) {
        await dispatch(fetchAttendeesAttendance(activeConfId))
      }
    } catch (err) {
      console.error('Failed to decide attendance', err)
      toast.error('Could not update this attendance record.')
    } finally {
      setDecidingId(null)
    }
  }

  const handleStartReject = (recordId) => {
    setRejectingId(recordId)
    setRejectReason('')
  }

  const handleCancelReject = () => {
    setRejectingId(null)
    setRejectReason('')
  }

  const handleConfirmReject = async (recordId) => {
    await handleDecide(recordId, 'rejected', rejectReason.trim())
    setRejectingId(null)
    setRejectReason('')
  }

  const handleConfirmReApprove = async () => {
    if (!reApproveId) return
    await handleDecide(reApproveId, 'approved')
    setReApproveId(null)
  }

  const rows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    const list = []
    attendeesAttendance.forEach((attendee) => {
      const matchesName =
        !q ||
        attendee.firstName?.toLowerCase().includes(q) ||
        attendee.lastName?.toLowerCase().includes(q)
      if (!matchesName) return

      attendee.attendance.forEach(({ date, records }) => {
        if (activeDayFilter && date !== activeDayFilter) return
        records
          .filter((r) => r.status === 'pending' || r.status === 'rejected')
          .forEach((record) => {
            list.push({ attendee, date, record })
          })
      })
    })
    list.sort((a, b) => (b.record.scanTime ?? 0) - (a.record.scanTime ?? 0))
    return list
  }, [attendeesAttendance, searchTerm, activeDayFilter])

  const pagedRows = useMemo(
    () => rows.slice(page * pageSize, (page + 1) * pageSize),
    [rows, page, pageSize]
  )

  useEffect(() => {
    setPage(0)
  }, [searchTerm, activeDayFilter])

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconButton onClick={goBack} className={classes.iconBtn}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" style={{ fontWeight: 700 }}>
            Pending & Rejected Attendance
          </Typography>
        </Box>
      </Box>

      <Wrapper inProgress={inProgress}>
        {attendeesAttendance?.length ? (
          <Box className={classes.tableContainer}>
            <Box className={classes.filterBar}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search by attendee name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 320 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: '#90a4ae', fontSize: 18 }} />
                    </InputAdornment>
                  )
                }}
              />
              <Chip
                label="All days"
                onClick={() => setActiveDayFilter(null)}
                color={activeDayFilter === null ? 'primary' : 'default'}
                style={{ fontWeight: activeDayFilter === null ? 700 : 400 }}
                clickable
              />
              {conferenceDates.map((date) => (
                <Chip
                  key={date}
                  label={moment(date, 'MM/DD/YYYY').format('MM/DD ddd')}
                  onClick={() => setActiveDayFilter(activeDayFilter === date ? null : date)}
                  color={activeDayFilter === date ? 'primary' : 'default'}
                  clickable
                />
              ))}
            </Box>

            {rows.length === 0 ? (
              <Box
                style={{
                  padding: '40px 24px',
                  textAlign: 'center',
                  color: '#90a4ae',
                  fontSize: 14
                }}>
                {activeDayFilter
                  ? `No pending/rejected attendance on ${moment(
                      activeDayFilter,
                      'MM/DD/YYYY'
                    ).format('MMM D, YYYY')}.`
                  : 'No pending or rejected attendance records.'}
              </Box>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f6fa' }}>
                      <th className={classes.headerCell} style={{ textAlign: 'left' }}>
                        Full Name
                      </th>
                      <th className={classes.headerCell} style={{ textAlign: 'left' }}>
                        Event / Session
                      </th>
                      <th className={classes.headerCell}>Scanned At</th>
                      <th className={classes.headerCell}>Status</th>
                      <th className={classes.headerCell} style={{ textAlign: 'left' }}>
                        Reason
                      </th>
                      <th className={classes.headerCell}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map(({ attendee, date, record }, rowIdx) => {
                      const rowBg = rowIdx % 2 === 0 ? '#fff' : '#fafafa'
                      const window = formatTargetWindow(record)
                      const dateLabel = date ? moment(date, 'MM/DD/YYYY').format('MMM D') : ''
                      return (
                        <tr key={record.id} style={{ backgroundColor: rowBg }}>
                          <td className={classes.bodyCell} style={{ backgroundColor: rowBg }}>
                            <span style={{ color: '#1565c0', fontWeight: 500 }}>
                              {attendee.lastName}, {attendee.firstName}
                            </span>
                            {attendee.email && (
                              <div style={{ fontSize: 12, color: '#78909c' }}>{attendee.email}</div>
                            )}
                          </td>
                          <td className={classes.bodyCell} style={{ backgroundColor: rowBg }}>
                            <div>{record.targetTitle}</div>
                            {window && (
                              <div style={{ fontSize: 12, color: '#66757c' }}>
                                {dateLabel && `${dateLabel}, `}
                                {window}
                              </div>
                            )}
                          </td>
                          <td
                            className={classes.bodyCell}
                            style={{
                              backgroundColor: rowBg,
                              textAlign: 'center',
                              whiteSpace: 'nowrap'
                            }}>
                            {record.scanTimeLabel || '-'}
                          </td>
                          <td
                            className={classes.bodyCell}
                            style={{ backgroundColor: rowBg, textAlign: 'center' }}>
                            <StatusChip status={record.status} />
                          </td>
                          <td className={classes.bodyCell} style={{ backgroundColor: rowBg }}>
                            {record.status === 'pending' && (
                              <Typography
                                variant="caption"
                                style={{ color: '#78909c', display: 'block' }}>
                                Attendee marked attendance after the grace time
                              </Typography>
                            )}
                            {record.decisionHistory?.length > 0 &&
                              record.decisionHistory.map((h, idx) => {
                                const decidedByLabel =
                                  h.decidedBy === user?.id ? 'You' : h.decidedByName || 'an admin'
                                if (h.decision === 'rejected') {
                                  return (
                                    <Box key={idx} style={{ display: 'block' }}>
                                      {h.reason && (
                                        <Typography
                                          variant="caption"
                                          style={{ color: '#c62828', display: 'block' }}>
                                          {h.reason}
                                        </Typography>
                                      )}
                                      <Typography
                                        variant="caption"
                                        style={{ color: '#78909c', display: 'block' }}>
                                        Rejected by: {decidedByLabel}
                                      </Typography>
                                    </Box>
                                  )
                                }
                                return (
                                  <Typography
                                    key={idx}
                                    variant="caption"
                                    style={{ color: '#78909c', display: 'block' }}>
                                    Approved
                                    {h.decidedBy !== user?.id
                                      ? ` by ${h.decidedByName || 'an admin'}`
                                      : ''}
                                    {h.decidedAt?.toDate
                                      ? ` on ${moment(h.decidedAt.toDate()).format(
                                          'MMM D, h:mm A'
                                        )}`
                                      : ''}
                                  </Typography>
                                )
                              })}
                          </td>
                          <td
                            className={classes.bodyCell}
                            style={{
                              backgroundColor: rowBg,
                              textAlign: 'center',
                              whiteSpace: 'nowrap'
                            }}>
                            {record.status === 'pending' && (
                              <Box style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  disabled={Boolean(decidingId)}
                                  onClick={() => handleDecide(record.id, 'approved')}>
                                  Approve
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  disabled={Boolean(decidingId)}
                                  onClick={() => handleStartReject(record.id)}>
                                  Reject
                                </Button>
                              </Box>
                            )}
                            {record.status === 'rejected' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                disabled={Boolean(decidingId)}
                                onClick={() => setReApproveId(record.id)}>
                                Approve
                              </Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              rowsPerPage={pageSize}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10))
                setPage(0)
              }}
            />
          </Box>
        ) : (
          !inProgress && (
            <ResourceNotFound message="No attendance data available for this conference" />
          )
        )}
      </Wrapper>

      <ConfirmationModal
        open={Boolean(reApproveId)}
        closeModal={() => setReApproveId(null)}
        dialogTitle="Approve Attendance"
        dialogText="This record was rejected earlier. Approve it now?"
        inProgress={decidingId === reApproveId}
        handleConfirm={handleConfirmReApprove}
      />

      <Dialog open={Boolean(rejectingId)} onClose={handleCancelReject} fullWidth maxWidth="xs">
        <DialogTitle>Reject attendance</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            multiline
            minRows={3}
            autoFocus
            placeholder="Optional reason for rejecting (max 200 characters)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value.slice(0, 200))}
            helperText={`${rejectReason.length}/200`}
            FormHelperTextProps={{
              style: { textAlign: 'right', margin: 0, fontSize: 11 }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReject} disabled={Boolean(decidingId)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            style={{ color: '#fff' }}
            disabled={Boolean(decidingId)}
            onClick={() => handleConfirmReject(rejectingId)}>
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PendingAttendancePage

const useStyles = makeStyles({
  container: {
    padding: '0.5rem',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '1rem',
    marginBottom: '1rem'
  },
  iconBtn: {
    padding: '4px'
  },
  tableContainer: {
    marginTop: 12,
    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
    borderRadius: '8px',
    overflow: 'hidden',
    border: 'none',
    backgroundColor: '#fff'
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    padding: '12px 16px',
    alignItems: 'center',
    borderBottom: '1px solid rgba(0,0,0,0.06)'
  },
  headerCell: {
    padding: '12px 10px',
    borderBottom: '2px solid #e0e0e0',
    borderRight: '1px solid #f0f0f0',
    fontWeight: 600,
    fontSize: 13,
    color: '#455a64',
    textAlign: 'center',
    whiteSpace: 'nowrap'
  },
  bodyCell: {
    padding: '10px 12px',
    borderBottom: '1px solid #f0f0f0',
    borderRight: '1px solid #f5f5f5',
    fontSize: 13,
    verticalAlign: 'top'
  }
})
